import type { PairedPhone } from './phone-sync.js';

export interface PhoneToast {
  id: string;
  text: string;
  tone: 'busy' | 'done' | 'info' | 'error';
}

/**
 * What changed between two snapshots of the paired phones, as short notices
 * for the page's toasts (#35): trips arriving on a phone, phones pairing,
 * unpairing, coming online and going offline. Downloads in progress are not
 * changes; they are shown for as long as they last (see `busyToasts`).
 */
export function phoneChanges(previous: PairedPhone[] | undefined, next: PairedPhone[], tripName: (tripId: string) => string): PhoneToast[] {
  if (!previous) return [];
  const before = new Map(previous.map((phone) => [phone.id, phone]));
  const toasts: PhoneToast[] = [];
  for (const phone of next) {
    const old = before.get(phone.id);
    if (!old) {
      toasts.push({ id: `paired-${phone.id}`, text: `${phone.name} paired`, tone: 'done' });
      continue;
    }
    for (const [tripId, version] of Object.entries(phone.delivered)) {
      if (old.delivered[tripId] !== version) toasts.push({ id: `on-${phone.id}-${tripId}-${version}`, text: `${tripName(tripId)} is on ${phone.name}`, tone: 'done' });
    }
    if (phone.connected !== old.connected) {
      toasts.push({ id: `online-${phone.id}-${phone.connected}`, text: phone.connected ? `${phone.name} is online` : `${phone.name} went offline`, tone: 'info' });
    }
  }
  const remaining = new Set(next.map((phone) => phone.id));
  for (const phone of previous) if (!remaining.has(phone.id)) toasts.push({ id: `unpaired-${phone.id}`, text: `${phone.name} unpaired`, tone: 'info' });
  return toasts;
}

/**
 * Finished notices, each removed `showMs` after it appeared. The timers belong to
 * the board, not to the snapshot that produced the notice: later snapshots must
 * neither cancel them nor be needed to trigger them (#37).
 */
export class RecentToasts {
  private items: PhoneToast[] = [];
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(private readonly onChange: (items: PhoneToast[]) => void, private readonly showMs = 4000, private readonly limit = 4) {}

  add(changes: PhoneToast[]) {
    if (changes.length === 0) return;
    this.items = [...this.items.filter((item) => !changes.some((change) => change.id === item.id)), ...changes].slice(-this.limit);
    this.onChange(this.items);
    for (const change of changes) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        // A newer notice with the same id replaced this one and has its own timer.
        if (!this.items.includes(change)) return;
        this.items = this.items.filter((item) => item !== change);
        this.onChange(this.items);
      }, this.showMs);
      this.timers.add(timer);
    }
  }

  dispose() {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
  }
}

/** Work in progress right now, shown until it ends. */
export function busyToasts(phones: PairedPhone[] | undefined, tripName: (tripId: string) => string): PhoneToast[] {
  const toasts: PhoneToast[] = [];
  for (const phone of phones ?? []) {
    if (phone.downloading) toasts.push({ id: `sending-${phone.id}`, text: `Sending ${tripName(phone.downloading)} to ${phone.name}…`, tone: 'busy' });
  }
  return toasts;
}
